import { Component, ErrorInfo, ReactNode } from "react";

import { getTokenFromUrl } from "helpers/functions";
import store from "store/store";
import { palette } from "theme/palette";

type Props = {
  children: ReactNode;
  // Names the boundary in logs so we can tell a whole-widget crash from a
  // single-step crash without a stack trace.
  scope: string;
};

type State = {
  hasError: boolean;
};

/**
 * The booking widget previously had NO error boundary anywhere.
 *
 * That is worse here than in an ordinary React app: the widget mounts into a
 * shadow root from `connectedCallback` (index.tsx), and `createRoot` is only
 * ever called from there. When React 18 hits an uncaught render error it walks
 * to the HostRoot and unmounts the tree by rendering null — so `#sjd-form-root`
 * simply empties. Nothing re-runs `connectedCallback`, so there is no remount
 * path and no way back except a manual page reload. To the customer the booking
 * form just vanishes; to us it is completely silent, which is why crashes here
 * could persist for weeks without anyone seeing an error.
 *
 * This boundary converts that into a visible, recoverable state, and — because
 * the cart lives server-side behind a token — lets the customer resume the
 * booking they had in progress rather than starting over.
 *
 * Deliberately styled with plain inline styles rather than MUI: if the theme or
 * emotion cache is what failed, a fallback that depends on them fails too.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[sjd-error-boundary] ${this.props.scope} crashed`, {
      error,
      componentStack: info.componentStack,
      cartToken: ErrorBoundary.resolveCartToken(),
      href: window.location.href,
    });
  }

  /**
   * Read the cart token without hooks (class component) and without assuming
   * the store is healthy — easy-peasy's getState() is a plain module-level
   * call, independent of the React tree that just died. Falls back to the URL,
   * which is where useTokenCart looks first anyway.
   */
  static resolveCartToken(): string | null {
    try {
      const fromStore = store.getState().cartToken;
      if (fromStore) return fromStore;
    } catch {
      /* store unavailable — fall through to the URL */
    }
    try {
      return getTokenFromUrl();
    } catch {
      return null;
    }
  }

  handleReload = () => {
    const token = ErrorBoundary.resolveCartToken();
    if (!token) {
      window.location.reload();
      return;
    }
    // `?token=…` is the shape useTokenCart resumes from: it fetches the cart,
    // then navigates to the step the customer had reached.
    const url = new URL(window.location.href);
    url.searchParams.set("token", token);
    window.location.assign(url.toString());
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          margin: "24px auto",
          maxWidth: 520,
          padding: 24,
          border: `1px solid ${palette.grey}`,
          borderRadius: 8,
          textAlign: "center",
          fontFamily: "inherit",
          color: palette.black,
          background: palette.white,
        }}
      >
        <p style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600 }}>
          Something went wrong
        </p>
        <p style={{ margin: "0 0 20px", fontSize: 14, lineHeight: 1.5 }}>
          Sorry — the booking form hit an unexpected error. Your trip details
          have been saved, so you can pick up where you left off.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: "10px 20px",
            fontSize: 15,
            fontWeight: 600,
            color: palette.white,
            background: palette.accent,
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          Continue my booking
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
